import { ChangeEvent, FC, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import type { CompanyGroupRecord, CompanyLocationRecord } from '../admin/clients/api';
import { saveSharedCompanyGroup, saveSharedCompanyLocation } from './api';

type ResourceKind = 'locations' | 'groups';
type ImportRow = {
  rowNumber: number;
  values: Record<string, string>;
  errors: string[];
};

type Props = {
  resource: ResourceKind;
  tenantQuery: string;
  locations: CompanyLocationRecord[];
  groups: CompanyGroupRecord[];
  onLocationsImported: (records: CompanyLocationRecord[]) => void;
  onGroupsImported: (records: CompanyGroupRecord[]) => void;
  createLocation?: (payload: Record<string, string | boolean>) => Promise<CompanyLocationRecord>;
  createGroup?: (payload: Record<string, string | number | null>) => Promise<CompanyGroupRecord>;
};

const locationHeaders = ['Display Name', 'Code', 'Public Label', 'Location Group', 'Timezone', 'Active', 'Notes'];
const groupHeaders = ['Name', 'Slug', 'Type', 'Parent Department', 'Status'];

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
const slugify = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const normalizedValue = (value: unknown) => String(value ?? '').trim();
const truthy = new Set(['1', 'true', 'yes', 'y', 'active']);

const readRows = async (file: File): Promise<Record<string, string>[]> => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets.Import ?? workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];

  return (XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' }) ?? []).map(row =>
    Object.entries(row).reduce<Record<string, string>>((values, [key, value]) => {
      values[normalizeHeader(key)] = normalizedValue(value);
      return values;
    }, {})
  );
};

const downloadTemplate = (resource: ResourceKind) => {
  const headers = resource === 'locations' ? locationHeaders : groupHeaders;
  const example = resource === 'locations'
    ? ['Bethlehem', 'BE', 'Bethlehem Campus', 'Main campuses', 'America/New_York', 'Yes', 'Optional operational notes']
    : ['Department of Neurosciences', 'department-of-neurosciences', 'Department', '', 'Active'];
  const secondExample = resource === 'groups'
    ? ['Neurosurgery', 'neurosurgery', 'Schedule Group', 'Department of Neurosciences', 'Active']
    : null;
  const instructions = resource === 'locations'
    ? [
        ['CARES Locations Import Template'],
        ['Required columns', 'Display Name, Code'],
        ['Accepted file', '.xlsx generated from this template'],
        ['Behavior', 'Rows are previewed and validated before creation. Existing locations are never overwritten.'],
        ['Active values', 'Yes/No, True/False, 1/0'],
      ]
    : [
        ['CARES Department & Schedule Group Import Template'],
        ['Required columns', 'Name, Type'],
        ['Type values', 'Department or Schedule Group'],
        ['Parent Department', 'Required only when assigning a Schedule Group; leave blank to keep it Unassigned.'],
        ['Behavior', 'Rows are previewed and validated before creation. Existing records are never overwritten.'],
      ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(instructions), 'Instructions');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([headers, example, ...(secondExample ? [secondExample] : [])]), 'Import');
  XLSX.writeFile(workbook, resource === 'locations' ? 'cares-locations-import-template.xlsx' : 'cares-departments-schedule-groups-import-template.xlsx');
};

const CompanyResourceSpreadsheetImport: FC<Props> = ({
  resource,
  tenantQuery,
  locations,
  groups,
  onLocationsImported,
  onGroupsImported,
  createLocation,
  createGroup,
}) => {
  const [show, setShow] = useState(false);
  const [fileName, setFileName] = useState('');
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const preview = useMemo<ImportRow[]>(() => {
    const existingLocationCodes = new Set(locations.map(location => location.code.toLowerCase()));
    const existingLocationNames = new Set(locations.map(location => location.display_name.toLowerCase()));
    const existingGroupSlugs = new Set(groups.map(group => group.slug.toLowerCase()));
    const existingGroupNames = new Set(groups.map(group => group.name.toLowerCase()));
    const seenCodes = new Set<string>();
    const seenNames = new Set<string>();
    const seenSlugs = new Set<string>();
    const fileDepartments = new Set(
      rawRows
        .filter(row => (row.type || row.kind).toLowerCase() === 'department')
        .map(row => (row.name || '').toLowerCase())
        .filter(Boolean)
    );
    const knownDepartments = new Set([
      ...groups.filter(group => group.is_department).map(group => group.name.toLowerCase()),
      ...fileDepartments,
    ]);

    return rawRows.map((values, index) => {
      const errors: string[] = [];
      if (resource === 'locations') {
        const name = values.display_name || values.name || '';
        const code = values.code || '';
        if (!name) errors.push('Display Name is required.');
        if (!code) errors.push('Code is required.');
        if (name && (existingLocationNames.has(name.toLowerCase()) || seenNames.has(name.toLowerCase()))) errors.push('Display Name already exists.');
        if (code && (existingLocationCodes.has(code.toLowerCase()) || seenCodes.has(code.toLowerCase()))) errors.push('Code already exists.');
        if (name) seenNames.add(name.toLowerCase());
        if (code) seenCodes.add(code.toLowerCase());
      } else {
        const name = values.name || '';
        const slug = values.slug || slugify(name);
        const type = (values.type || values.kind || '').toLowerCase();
        const parent = (values.parent_department || values.department || '').toLowerCase();
        if (!name) errors.push('Name is required.');
        if (!['department', 'schedule group', 'schedule_group', 'group', 'team'].includes(type)) errors.push('Type must be Department or Schedule Group.');
        if (name && (existingGroupNames.has(name.toLowerCase()) || seenNames.has(name.toLowerCase()))) errors.push('Name already exists.');
        if (slug && (existingGroupSlugs.has(slug.toLowerCase()) || seenSlugs.has(slug.toLowerCase()))) errors.push('Slug already exists.');
        if (type !== 'department' && parent && !knownDepartments.has(parent)) errors.push('Parent Department is not present in CARES or this file.');
        if (type === 'department' && parent) errors.push('Departments cannot have a Parent Department.');
        if (name) seenNames.add(name.toLowerCase());
        if (slug) seenSlugs.add(slug.toLowerCase());
      }
      return { rowNumber: index + 2, values, errors };
    });
  }, [groups, locations, rawRows, resource]);

  const reset = () => {
    setFileName('');
    setRawRows([]);
    setMessage(null);
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setReading(true);
    setMessage(null);
    try {
      const rows = await readRows(file);
      setFileName(file.name);
      setRawRows(rows);
      if (rows.length === 0) setMessage('No data rows were found on the Import sheet.');
    } catch {
      reset();
      setMessage('The workbook could not be read. Download a fresh CARES template and try again.');
    } finally {
      setReading(false);
      event.target.value = '';
    }
  };

  const commit = async () => {
    if (preview.length === 0 || preview.some(row => row.errors.length > 0)) return;
    setSaving(true);
    setMessage(null);
    try {
      if (resource === 'locations') {
        const imported: CompanyLocationRecord[] = [];
        for (const row of preview) {
          const values = row.values;
          const payload = {
            code: values.code.toUpperCase(),
            display_name: values.display_name || values.name,
            public_label: values.public_label || '',
            location_group: values.location_group || values.department || '',
            timezone: values.timezone || 'America/New_York',
            is_active: values.active === '' ? true : truthy.has(values.active.toLowerCase()),
            notes: values.notes || '',
          };
          imported.push(await (createLocation
            ? createLocation(payload)
            : saveSharedCompanyLocation(tenantQuery, null, payload)));
        }
        onLocationsImported(imported);
      } else {
        const imported: CompanyGroupRecord[] = [];
        const departments = preview.filter(row => (row.values.type || row.values.kind).toLowerCase() === 'department');
        const scheduleGroups = preview.filter(row => !departments.includes(row));
        for (const row of [...departments, ...scheduleGroups]) {
          const values = row.values;
          const isDepartment = (values.type || values.kind).toLowerCase() === 'department';
          const parentName = values.parent_department || values.department || '';
          const parent = [...groups, ...imported].find(group => group.is_department && group.name.toLowerCase() === parentName.toLowerCase());
          const payload = {
            parent_group_id: isDepartment ? null : parent?.id ?? null,
            name: values.name,
            slug: values.slug || slugify(values.name),
            kind: isDepartment ? 'department' : 'team',
            status: ['active', 'inactive', 'archived'].includes((values.status || '').toLowerCase()) ? values.status.toLowerCase() : 'active',
          };
          imported.push(await (createGroup
            ? createGroup(payload)
            : saveSharedCompanyGroup(tenantQuery, null, payload)));
        }
        onGroupsImported(imported);
      }
      setMessage(`${preview.length} ${resource === 'locations' ? 'locations' : 'Departments / Schedule Groups'} imported successfully.`);
      setRawRows([]);
      setFileName('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Import stopped because a row could not be saved. Existing records were not overwritten.');
    } finally {
      setSaving(false);
    }
  };

  const errorCount = preview.filter(row => row.errors.length > 0).length;

  return (
    <>
      <div className="d-flex flex-wrap gap-2">
        <Button variant="outline-primary" onClick={() => downloadTemplate(resource)}>
          <i className="ri-file-excel-2-line me-1" /> Download Excel template
        </Button>
        <Button variant="outline-primary" onClick={() => setShow(true)}>
          <i className="ri-upload-2-line me-1" /> Import completed spreadsheet
        </Button>
      </div>

      <Modal show={show} onHide={() => setShow(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Import {resource === 'locations' ? 'Locations' : 'Departments & Groups'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            Download the CARES workbook, complete the <strong>Import</strong> sheet, and upload the saved `.xlsx` file. CARES validates every row before enabling import and never overwrites an existing record.
          </Alert>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button size="sm" variant="outline-primary" onClick={() => downloadTemplate(resource)}>Download fresh template</Button>
            <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFile} disabled={reading || saving} style={{ maxWidth: '360px' }} />
            {rawRows.length > 0 ? <Button size="sm" variant="light" onClick={reset}>Clear</Button> : null}
          </div>
          {reading ? <Alert variant="light"><Spinner size="sm" animation="border" className="me-2" />Reading workbook…</Alert> : null}
          {message ? <Alert variant={message.includes('successfully') ? 'success' : 'warning'}>{message}</Alert> : null}
          {fileName ? <p className="text-muted fs-12">Previewing {fileName} · {preview.length} rows · {errorCount} with errors</p> : null}
          {preview.length > 0 ? (
            <div className="table-responsive border rounded">
              <Table hover className="mb-0 align-middle">
                <thead><tr><th>Row</th><th>Name</th><th>Code / Slug</th><th>Type / Department</th><th>Status</th></tr></thead>
                <tbody>
                  {preview.map(row => (
                    <tr key={row.rowNumber}>
                      <td>{row.rowNumber}</td>
                      <td>{row.values.display_name || row.values.name || '—'}</td>
                      <td>{row.values.code || row.values.slug || (row.values.name ? slugify(row.values.name) : '—')}</td>
                      <td>{resource === 'locations' ? row.values.location_group || '—' : `${row.values.type || row.values.kind || '—'}${row.values.parent_department ? ` · ${row.values.parent_department}` : ''}`}</td>
                      <td>{row.errors.length > 0 ? <><Badge bg="danger">Needs correction</Badge><div className="text-danger fs-12 mt-1">{row.errors.join(' ')}</div></> : <Badge bg="success">Ready</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShow(false)}>Close</Button>
          <Button variant="primary" onClick={() => void commit()} disabled={saving || preview.length === 0 || errorCount > 0}>
            {saving ? 'Importing…' : `Import ${preview.length || ''} rows`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export const CompanyResourceBulkSetup: FC<Props> = (props) => (
  <Card className="custom-card mt-3">
    <Card.Header className="py-2">
      <div className="card-title mb-0">Bulk Setup</div>
    </Card.Header>
    <Card.Body className="p-3">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <div>
          <div className="fw-semibold">
            Add multiple {props.resource === 'locations' ? 'locations' : 'Departments and Schedule Groups'}
          </div>
          <div className="text-muted fs-12">
            Download the CARES template, complete it, and upload it here. Every row is validated before anything is saved.
          </div>
        </div>
        <CompanyResourceSpreadsheetImport {...props} />
      </div>
    </Card.Body>
  </Card>
);

export default CompanyResourceSpreadsheetImport;
